import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import api from "../api";
import {
  formatUkDateTime,
  stampToMillis,
  statusChipColor,
  statusLabel,
} from "../utils/mobileSubscriptionFormat";
import BillingFilterBar, { matchesSearch, normalisePaymentStatus } from "./BillingFilterBar";
import BillingPagination, { useBillingPagination } from "./BillingPagination";

/**
 * "All" billing tab: every transaction the account has, newest first.
 *
 * Billing is split across four tabs, each backed by its own endpoint, so a parent whose only
 * purchase was a mock exam landed on the Course tab and saw "No subscriptions found" — the
 * money was one tab over. This merges all four into a single timeline. It reuses the existing
 * endpoints rather than adding a new aggregate one, so the per-tab views stay the source of
 * truth and there is only one place where each purchase type is queried.
 *
 * Deliberately read-only: receipts and assign-to-student stay on the owning tab, where the
 * bespoke params each receipt endpoint needs are already handled.
 */

const PARENT_SOURCES = [
  { key: "course", url: "parent/subscriptions" },
  { key: "mock", url: "parent/mock-exam/purchases" },
  // only=paper: this endpoint also returns mock exams and course payments by
  // default, which would list every mock exam twice in this merged view.
  { key: "paper", url: "parent/paper-purchases?only=paper" },
  { key: "app", url: "parent/mobile-subscriptions/history" },
];

const STUDENT_SOURCES = [
  { key: "mock", url: "student/mock-exam/purchases" },
  { key: "paper", url: "student/paper/my-purchases?only=paper" },
  { key: "app", url: "student/subscription/history" },
];

const KIND_META = {
  Course: { color: "#667eea" },
  App: { color: "#0ea5e9" },
  "Mock Exam": { color: "#d32f2f" },
  Paper: { color: "#1565c0" },
  Bundle: { color: "#f59e0b" },
};

const money = (amount, currency) => {
  const value = Number(amount || 0);
  const code = String(currency || "gbp").toLowerCase();
  const symbol = code === "gbp" || code === "£" ? "£" : `${code.toUpperCase()} `;
  return `${symbol}${value.toFixed(2)}`;
};

/** The API returns `data` sometimes at the root and sometimes nested; accept both. */
const payloadOf = (response) => {
  const body = response?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return body?.data ?? [];
};

const courseRows = (groups) => {
  if (!Array.isArray(groups)) return [];
  const rows = [];
  groups.forEach((group) => {
    (group?.subscriptions || []).forEach((sub) => {
      (sub?.payments || []).forEach((payment, index) => {
        // Unpaid installments are commitments, not transactions — the Course tab shows those
        // with their Pay button. "All" is a record of what has actually been charged.
        if (String(payment?.status || "").toUpperCase() !== "PAID") return;
        rows.push({
          id: `course-${sub.course_id}-${sub.course_price_id ?? "n"}-${index}`,
          kind: "Course",
          title: sub.course_name || "Course",
          subtitle: [payment.term_name || payment.payment_type, sub.plan_name]
            .filter(Boolean)
            .join(" · "),
          person: sub.assigned_student_name || group.student_name,
          amount: payment.paid_amount ?? payment.amount_due,
          currency: payment.currency,
          status: "paid",
          date: payment.date_paid || payment.due_date,
        });
      });
    });
  });
  return rows;
};

const purchaseRows = (items, kindWhenNotBundle) => {
  if (!Array.isArray(items)) return [];
  // Opening an item under a Mobile Plan (or claiming a free one) writes a £0 row. Those are
  // access, not charges, and a plan holder has hundreds of them burying the real purchases.
  return items.filter((item) => Number(item?.amount ?? item?.price ?? 0) > 0).map((item, index) => {
    const isBundle = String(item?.type || "") === "bundle";
    const title =
      item.exam_name || item.paper_name || item.name || item.bundle_name || "Purchase";
    return {
      id: `${kindWhenNotBundle}-${item.purchase_id ?? index}`,
      kind: isBundle ? "Bundle" : kindWhenNotBundle,
      title,
      subtitle: [item.school_name, item.category, item.paper_category]
        .filter((part) => part && part !== "Bundle")
        .join(" · "),
      person: item.student_name,
      amount: item.price ?? item.amount,
      currency: item.currency,
      status: item.payment_status,
      date: item.purchased_at,
    };
  });
};

const appRows = (payload) => {
  // Parent returns { children: [{ child_name, history: [...] }] }; the student endpoint
  // returns that student's rows directly.
  const buckets = Array.isArray(payload?.children)
    ? payload.children.map((child) => ({ person: child.child_name, rows: child.history || [] }))
    : [{ person: null, rows: Array.isArray(payload) ? payload : [] }];

  const rows = [];
  buckets.forEach(({ person, rows: history }) => {
    (history || []).forEach((row, index) => {
      // Only actual charges; `subscription` rows describe a period, not a payment.
      if (String(row?.row_type || "") !== "payment") return;
      rows.push({
        id: `app-${row.id ?? index}-${person ?? "self"}`,
        kind: "App",
        title: row.plan?.name || "App subscription",
        subtitle: [row.payment_platform, row.payment_method].filter(Boolean).join(" · "),
        person,
        amount: Number(row.amount_pence || 0) / 100,
        currency: row.currency,
        status: row.status,
        date: row.paid_at || row.created_at,
      });
    });
  });
  return rows;
};

/**
 * @param {"parent"|"student"} scope  Which endpoint set to read.
 * @param {string[]|null} only        Restrict to these source keys ("course"|"mock"|"paper"|"app"),
 *                                    so the per-type tabs reuse this same renderer.
 */
const BillingAllPurchases = ({ scope = "parent", only = null }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failedSources, setFailedSources] = useState([]);
  const [activeKind, setActiveKind] = useState("All");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const {
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    paginate,
  } = useBillingPagination(`${activeKind}|${search}|${statusFilter}`);
  const navigate = useNavigate();

  // Where each row's Receipt / Details actions live. The parent portal calls the App tab
  // "mobile"; the student portal has no Course tab.
  const portal = scope === "student"
    ? process.env.REACT_APP_STUDENT_PREFIX || "student"
    : process.env.REACT_APP_PARENT_PREFIX || "parent";
  const tabFor = (row) => {
    if (row.source === "app") return scope === "student" ? "app" : "mobile";
    return row.source;
  };

  const allSources = scope === "student" ? STUDENT_SOURCES : PARENT_SOURCES;
  const sources = useMemo(
    () => (only ? allSources.filter((s) => only.includes(s.key)) : allSources),
    // `only` is a literal at every call site, so compare by value rather than identity.
    [allSources, only && only.join(",")] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const load = useCallback(async () => {
    setLoading(true);
    // allSettled, not all: one dead endpoint must not blank the whole timeline. Whatever
    // failed is named in a banner so an empty list is never silently wrong.
    const results = await Promise.allSettled(sources.map((source) => api.get(source.url)));

    const collected = [];
    const failed = [];

    results.forEach((result, index) => {
      const source = sources[index];
      if (result.status !== "fulfilled") {
        failed.push(source.key);
        return;
      }
      const payload = payloadOf(result.value);
      const tag = (rows) => rows.map((row) => ({ ...row, source: source.key }));
      if (source.key === "course") collected.push(...tag(courseRows(payload)));
      if (source.key === "mock") collected.push(...tag(purchaseRows(payload, "Mock Exam")));
      if (source.key === "paper") collected.push(...tag(purchaseRows(payload, "Paper")));
      if (source.key === "app") collected.push(...tag(appRows(payload)));
    });

    collected.sort((a, b) => stampToMillis(b.date) - stampToMillis(a.date));
    setRows(collected);
    setFailedSources(failed);
    setLoading(false);
  }, [sources]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const kinds = useMemo(() => {
    const present = [];
    rows.forEach((row) => {
      if (!present.includes(row.kind)) present.push(row.kind);
    });
    return ["All", ...present];
  }, [rows]);

  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          (activeKind === "All" || row.kind === activeKind) &&
          (!statusFilter || normalisePaymentStatus(row.status) === statusFilter) &&
          matchesSearch(search, [row.title, row.subtitle, row.person, row.kind])
      ),
    [rows, activeKind, statusFilter, search]
  );
  const { page, count: rowCount, rows: pagedRows } = paginate(visibleRows);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6, gap: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ color: "#5a6c7d" }}>
          Loading your payments…
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {failedSources.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some payments could not be loaded ({failedSources.join(", ")}). The individual tabs may
          show more.
        </Alert>
      )}

      {rows.length > 0 && (
        <BillingFilterBar
          search={search}
          onSearchChange={setSearch}
          status={statusFilter}
          onStatusChange={setStatusFilter}
          placeholder="Search by name, student, type..."
        />
      )}

      {kinds.length > 2 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
          {kinds.map((kind) => (
            <Chip
              key={kind}
              label={kind}
              size="small"
              onClick={() => setActiveKind(kind)}
              variant={activeKind === kind ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                ...(activeKind === kind
                  ? { bgcolor: KIND_META[kind]?.color || "#667eea", color: "#fff" }
                  : {}),
              }}
            />
          ))}
        </Stack>
      )}

      {visibleRows.length === 0 ? (
        <Box
          sx={{
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 3,
            py: 6,
            textAlign: "center",
          }}
        >
          <AccountBalanceWalletIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#475569" }}>
            {rows.length > 0 ? "No matching payments" : "No payments yet"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#94a3b8", mt: 0.5 }}>
            {rows.length > 0
              ? "No payments match your search or status filter."
              : only
              ? "Nothing here yet."
              : "Anything you buy — courses, mock exams, papers, bundles or the app plan — appears here."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ bgcolor: "#fff", borderRadius: 3, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
          {pagedRows.map((row, index) => (
            <Box key={row.id}>
              {index > 0 && <Divider />}
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 1, sm: 2 },
                  px: { xs: 2, sm: 3 },
                  py: 2,
                }}
              >
                <Chip
                  label={row.kind}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    minWidth: 92,
                    bgcolor: `${KIND_META[row.kind]?.color || "#667eea"}18`,
                    color: KIND_META[row.kind]?.color || "#667eea",
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#111827" }}>
                    {row.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                    {[row.subtitle, row.person].filter(Boolean).join(" · ") || "—"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: { xs: "left", sm: "right" }, flexShrink: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                    {money(row.amount, row.currency)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    {formatUkDateTime(row.date)}
                  </Typography>
                </Box>
                <Chip
                  label={statusLabel(row.status)}
                  size="small"
                  color={statusChipColor(row.status)}
                  sx={{ fontWeight: 600, flexShrink: 0 }}
                />
                {!only && (
                  // Receipts, assign-to-student and the full breakdown already live on each
                  // type's own tab; this opens it rather than duplicating them here.
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/${portal}/billing/${tabFor(row)}`)}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, flexShrink: 0 }}
                  >
                    Details
                  </Button>
                )}
              </Box>
            </Box>
          ))}
          <BillingPagination
            count={rowCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      )}
    </Box>
  );
};

export default BillingAllPurchases;
